
import React, { useState, useRef } from "react";
import "dayjs/locale/de";
// Restore cached result if not expired
import {
    Box,
    Typography,
    TextField,
    Button,
    Card,
    CardContent,
    Alert
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { db } from "../../firebase";
import { collection, doc, getDoc, getDocs, query, where } from "firebase/firestore";
import * as pdfjsLib from "pdfjs-dist";
import { normalizePdfUrl } from "./SearcherPdfsNamesPPB";

function isFourDigit(str) {
    return /^\d{4}$/.test(str);
}

export default PlansChoicePPBF;

// Returns an array of possible daycodes for a date, ordered from most specific to most general
function getDaycodesForDate(date) {
    const d = dayjs(date);
    const weekday = d.day(); // 0=Sunday, 1=Monday, ...
    switch (weekday) {
        case 1: // Monday
            return ["Mo", "Mo-Mi", "Mo-Do", "Mo-Fr"];
        case 2: // Tuesday
            return ["Di", "Mo-Mi", "Mo-Do", "Mo-Fr"];
        case 3: // Wednesday
            return ["Mi", "Mo-Mi", "Mo-Do", "Mo-Fr"];
        case 4: // Thursday
            return ["Do", "Mo-Do", "Mo-Fr"];
        case 5: // Friday
            return ["Fr", "Mo-Fr"];
        case 6: // Saturday
            return ["Sa"];
        case 0: // Sunday/Feiertag
            return ["SF"];
        default:
            return [];
    }
}



function PlansChoicePPBF() {
    const [pickedDate, setPickedDate] = useState(dayjs());
    const [dienstNummer, setDienstNummer] = useState("");
    const [searching, setSearching] = useState(false);
    const [error, setError] = useState("");
    const [result, setResult] = useState(null);
    const [pageBitmaps, setPageBitmaps] = useState([]);
    const [searchInfo, setSearchInfo] = useState(null);
    const pdfContainerRef = useRef(null);

    React.useEffect(() => {
        const cached = localStorage.getItem("plansChoicePPBFResult");
        if (!cached) return;
        try {
            const parsed = JSON.parse(cached);
            const ageHours = (Date.now() - parsed.timestamp) / (1000 * 60 * 60);
            if (ageHours < 15) {
                setResult(parsed.result);
                setPageBitmaps(parsed.pageBitmaps);
                setSearchInfo(parsed.searchInfo || null);
            } else {
                localStorage.removeItem("plansChoicePPBFResult");
            }
        } catch (e) {
            console.error("Cache parse error", e);
        }
    }, []);



    const handleSearch = async () => {
        setSearching(true);
        setError("");
        setResult(null);
        setPageBitmaps([]);
        try {
            // 0. Check Feiertage for picked date
            const picked = dayjs(pickedDate);
            const pickedStr = picked.format("YYYY-MM-DD");
            let overrideDaycode = null;
            try {
                const q = query(collection(db, "Feiertage"), where("date", "==", pickedStr));
                const feiertagSnap = await getDocs(q);
                if (!feiertagSnap.empty) {
                    const docData = feiertagSnap.docs[0].data();
                    if (docData.daycode) overrideDaycode = docData.daycode;
                }
            } catch (e) {
                // ignore Feiertage errors, fallback to normal
            }
            // 1. Find FahrplaenePages doc for pickedDate
            const pagesSnap = await getDocs(collection(db, "FahrplaenePages"));
            let foundDoc = null;
            let foundDocId = null;
            pagesSnap.docs.some(docSnap => {
                const data = docSnap.data();
                if (data.startDate && data.endDate) {
                    const start = dayjs(data.startDate).startOf('day');
                    const end = dayjs(data.endDate).startOf('day');
                    if (picked.isSameOrAfter(start, 'day') && picked.isSameOrBefore(end, 'day')) {
                        foundDoc = data;
                        foundDocId = docSnap.id;
                        return true; // stop searching after first match
                    }
                }
                return false;
            });
            if (!foundDoc || !foundDocId) {
                setError("Kein Fahrplan für das gewählte Datum gefunden.");
                setSearching(false);
                return;
            }
            // 2. Check dienstNummerList
            if (!foundDoc.dienstNummerList || !foundDoc.dienstNummerList.includes(dienstNummer)) {
                setError("DienstNummer nicht gefunden.");
                setSearching(false);
                return;
            }
            // 3. Check subcollection for dienstNummer
            const subColRef = collection(db, "FahrplaenePages", foundDocId, dienstNummer);
            const subColSnap = await getDocs(subColRef);
            let possibleDaycodes = [];
            if (overrideDaycode) {
                possibleDaycodes = [overrideDaycode];
            } else {
                possibleDaycodes = getDaycodesForDate(picked);
            }
            let pageEntry = null;
            // Helper: check if a single day is included in a range
            function isDayInRange(day, range) {
                const map = {
                    "Mo-Fr": ["Mo", "Di", "Mi", "Do", "Fr"],
                    "Mo-Do": ["Mo", "Di", "Mi", "Do"],
                    "Mo-Mi": ["Mo", "Di", "Mi"]
                };
                return map[range]?.includes(day);
            }

            // Try exact match first
            for (const code of possibleDaycodes) {
                subColSnap.forEach(docSnap => {
                    const data = docSnap.data();
                    if (!pageEntry && data.daycode === code) {
                        pageEntry = data;
                    }
                });
                if (pageEntry) break;
            }
            // If not found, try inclusion in range (for single days only)
            if (!pageEntry && possibleDaycodes.length > 0) {
                const singleDay = possibleDaycodes[0];
                const validDays = ["Mo", "Di", "Mi", "Do", "Fr"];
                if (validDays.includes(singleDay)) {
                    subColSnap.forEach(docSnap => {
                        const data = docSnap.data();
                        if (!pageEntry && isDayInRange(singleDay, data.daycode)) {
                            pageEntry = data;
                        }
                    });
                }
            }
            if (!pageEntry) {
                setError("Kein passender Tagcode für diesen Dienst gefunden.");
                setSearching(false);
                return;
            }
            // 4. Find Fahrplaene doc for pickedDate
            const fahrplaeneSnap = await getDocs(collection(db, "Fahrplaene"));
            let fahrplanDoc = null;
            let fahrplanDocId = null;
            fahrplaeneSnap.forEach(docSnap => {
                const data = docSnap.data();
                if (data.dateStart && data.dateEnd) {
                    const start = dayjs(data.dateStart).startOf('day');
                    const end = dayjs(data.dateEnd).startOf('day');
                    if (picked.startOf('day').isSameOrAfter(start) && picked.startOf('day').isSameOrBefore(end)) {
                        fahrplanDoc = data;
                        fahrplanDocId = docSnap.id;
                    }
                }
            });
            if (!fahrplanDoc || !fahrplanDocId) {
                setError("Kein PDF für das gewählte Datum gefunden.");
                setSearching(false);
                return;
            }
            // 5. Find pdfs subcollection with dienstLinie matching first two digits
            const dienstLiniePrefix = dienstNummer.substring(0, 2);
            const pdfsSnap = await getDocs(collection(db, "Fahrplaene", fahrplanDocId, "pdfs"));
            let pdfDoc = null;
            pdfsSnap.forEach(docSnap => {
                const data = docSnap.data();
                if (Array.isArray(data.dienstLinie)) {
                    if (data.dienstLinie.includes(dienstLiniePrefix)) {
                        pdfDoc = data;
                    }
                } else if (typeof data.dienstLinie === "string") {
                    if (data.dienstLinie === dienstLiniePrefix) {
                        pdfDoc = data;
                    }
                }
            });
            if (!pdfDoc) {
                setError("Kein PDF mit passender DienstLinie gefunden.");
                setSearching(false);
                return;
            }
            // 6. Show result
            const searchInfoObj = {
                pickedDate: picked.format("DD-MM-YYYY"),
                daycode: pageEntry.daycode
            };
            setResult({
                pdfUrl: pdfDoc.pdfUrl,
                startPage: pageEntry.startPage,
                endPage: pageEntry.endPage,
                dienstNummer,
                daycode: pageEntry.daycode
            });
            setSearchInfo(searchInfoObj);
            // Render PDF pages as bitmaps
            try {
                const normalizedUrl = normalizePdfUrl(pdfDoc.pdfUrl);
                const loadingTask = pdfjsLib.getDocument(normalizedUrl);
                const pdf = await loadingTask.promise;
                const bitmaps = [];
                for (let pageNum = pageEntry.startPage; pageNum <= pageEntry.endPage; pageNum++) {
                    const page = await pdf.getPage(pageNum);
                    const viewport = page.getViewport({ scale: 1.5 });
                    const canvas = document.createElement("canvas");
                    const context = canvas.getContext("2d");
                    canvas.width = viewport.width;
                    canvas.height = viewport.height;
                    await page.render({ canvasContext: context, viewport }).promise;
                    bitmaps.push(canvas.toDataURL());
                }
                setPageBitmaps(bitmaps);
                // Save to localStorage for 15 hours
                localStorage.setItem(
                    "plansChoicePPBFResult",
                    JSON.stringify({
                        result: {
                            pdfUrl: pdfDoc.pdfUrl,
                            startPage: pageEntry.startPage,
                            endPage: pageEntry.endPage,
                            dienstNummer,
                            daycode: pageEntry.daycode
                        },
                        pageBitmaps: bitmaps,
                        searchInfo: searchInfoObj,
                        timestamp: Date.now()
                    })
                );
            } catch (e) {
                setError("PDF konnte nicht gerendert werden: " + (e.message || e));
            }
        } catch (e) {
            setError("Fehler bei der Suche: " + (e.message || e));
        }
        setSearching(false);
    };

    return (
        <Box sx={{ maxWidth: 500, mx: "auto", mt: 4, border: '2px solid darkblue', borderRadius: 2, p: 2 }}>
            <Card>
                <CardContent>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Typography variant="h5" fontWeight={700}>Fahrpläne PPBF</Typography>
                        <Typography variant="body1">{dayjs().format("DD.MM.YYYY")}</Typography>
                    </Box>
                    <Box sx={{ mt: 2 }}>
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="de">
                            <DatePicker
                                label="Datum wählen"
                                value={pickedDate}
                                onChange={setPickedDate}
                                format="DD.MM.YYYY"
                                slotProps={{ textField: { fullWidth: true } }}
                            />
                        </LocalizationProvider>
                    </Box>
                    <Box sx={{ mt: 2 }}>
                        <TextField
                            label="DienstNummer"
                            value={dienstNummer}
                            onChange={e => setDienstNummer(e.target.value.replace(/\D/g, "").slice(0, 4))}
                            fullWidth
                            helperText="Dienst 4-stellig"
                            inputProps={{ maxLength: 4 }}
                        />
                    </Box>
                    <Box sx={{ mt: 2 }}>
                        <Button
                            variant="contained"
                            fullWidth
                            disabled={!isFourDigit(dienstNummer) || searching}
                            onClick={handleSearch}
                        >
                            START
                        </Button>
                    </Box>
                    {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
                </CardContent>
            </Card>
            {result && (
                <Card sx={{ mt: 3 }}>
                    <CardContent>
                        {searchInfo && (
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle1" color="primary" fontWeight={600}>
                                    {searchInfo.daycode} {searchInfo.pickedDate}
                                </Typography>
                            </Box>
                        )}
                        <Box ref={pdfContainerRef} sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {pageBitmaps.length > 0 && pageBitmaps.map((src, idx) => (
                                <img key={idx} src={src} alt={`PDF Seite ${result.startPage + idx}`} style={{ width: '100%', border: '1px solid #ccc', borderRadius: 4 }} />
                            ))}
                        </Box>
                    </CardContent>
                </Card>
            )}
        </Box>
    );
}
